import React, { useState, useEffect } from "react";
import RemainingDaysBadge from "./common/RemainingDaysBadge";
import AdExtensionModal from "./AdExtensionModal";
import ShoppingKeywordEditModal from "./ShoppingKeywordEditModal";
import QuantityIncreaseModal from "./QuantityIncreaseModal";
import TabNavigation from "../../../../../components/common/tabNavigation";
import RankCheckModal from "./RankCheckModal";
import AdEndRequestModal from "./AdEndRequestModal";
import {
  rejectAdAPI,
  getMemberAdAPI,
  getMissionHistoryAPI,
  getKeywordNameAPI,
  getNaverSmartStoreAPI,
  getOriginalInfoMissionAPI,
  patchAdExtensionAPI,
} from "../../../../../api/index";
import {
  convertUTCToKoreaTime,
  isAdExtensionLockedByEndSubmit,
  getMsUntilAdExtensionLock,
  resolveEndSubmitTimeForDate,
  ORIGIN_INFO_MEMO_KEYS,
  pickOriginInfoMemo,
  extractProductIdFromUrl,
  decodeHtmlText,
} from "../../../../../utils/helpers";
import { useAuth } from "../../../../../hooks/useAuth";
import { useZoomSettings } from "../../../../../hooks/useZoomSettings";
import "../AdDetailModal.css";

const ShoppingAdDetail = ({
  isOpen,
  onClose,
  adData,
  keywordData, // MyAdvertisement에서 전달받은 키워드 데이터
  priceGradeData, // MyAdvertisement에서 전달받은 가격 등급 데이터
  getUserDetailAPI, // MyAdvertisement에서 전달받은 사용자 상세 정보 API 함수
  onSave, // 키워드 수정 완료 시 호출될 콜백
}) => {
  const { user } = useAuth();
  const { settings: zoomSettings } = useZoomSettings();

  const endSubmitTime =
    zoomSettings?.end_submit != null &&
    String(zoomSettings.end_submit).match(/^\d{1,2}:\d{2}$/)
      ? zoomSettings.end_submit
      : "16:00";

  const isExtensionLocked =
    adData?.status === "APPROVED" &&
    isAdExtensionLockedByEndSubmit(adData?.endDate, zoomSettings);

  useEffect(() => {
    if (process.env.NODE_ENV === "development" && zoomSettings) {
      console.log("zoomSettings?.end_submit", zoomSettings?.end_submit);
    }
  }, [zoomSettings]);

  // 광고마감일 end_submit 도래 시 상세 화면 리프레시 (잠금 반영)
  useEffect(() => {
    if (!isOpen || !adData?.endDate || adData?.status !== "APPROVED") {
      return undefined;
    }
    const msUntilLock = getMsUntilAdExtensionLock(adData.endDate, zoomSettings);
    if (msUntilLock == null || msUntilLock <= 0) return undefined;
    // setTimeout 최대치 보호 (~24.8일)
    if (msUntilLock > 2147483647) return undefined;

    const timer = setTimeout(() => {
      window.location.reload();
    }, msUntilLock);

    return () => clearTimeout(timer);
  }, [isOpen, adData?.endDate, adData?.status, zoomSettings]);
  const [isExtensionModalOpen, setIsExtensionModalOpen] = useState(false);
  const [isKeywordEditModalOpen, setIsKeywordEditModalOpen] = useState(false);
  const [isQuantityIncreaseModalOpen, setIsQuantityIncreaseModalOpen] =
    useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isAdEndRequestModalOpen, setIsAdEndRequestModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("details"); // 탭 상태
  const [isRankCheckModalOpen, setIsRankCheckModalOpen] = useState(false);
  const [historyData, setHistoryData] = useState([]); // 내역 데이터
  const [historyLoading, setHistoryLoading] = useState(false); // 내역 로딩
  const [expandedHistoryIds, setExpandedHistoryIds] = useState(new Set()); // 펼쳐진 내역 ID
  const [keywordMap, setKeywordMap] = useState({}); // 키워드 ID -> 이름 맵
  const [refreshLoading, setRefreshLoading] = useState(false); // 다시 받아오기 로딩

  // 1회 단가 계산 - 결제금액 ÷ 신청수량 (기존 광고의 실제 단가)
  const getUnitPrice = (adData) => {
    const totalPrice = adData.price || 0;
    const totalQuantity = adData.totalQuantity || 0;
    return totalQuantity > 0 ? Math.round(totalPrice / totalQuantity) : 0;
  };

  const unitPrices = {
    [adData.ad_id || adData.id]: getUnitPrice(adData),
  };

  // username은 adData에서 가져오기 (광고 소유자 정보)
  const username = adData.username || adData.member_id || "";

  // 가격 등급 데이터는 props로 받은 것 사용
  const userData = {
    price_grade_detail: {
      data: priceGradeData || [],
    },
  };

  // 내역 데이터 로드 (내역 탭이 활성화될 때)
  useEffect(() => {
    if (activeTab === "history" && isOpen && adData?.ad_id) {
      loadHistoryData();
    }
  }, [activeTab, isOpen, adData?.ad_id]);

  // 내역 데이터 로드 함수
  const loadHistoryData = async () => {
    if (!adData?.ad_id) return;

    try {
      setHistoryLoading(true);
      const response = await getMissionHistoryAPI(adData.ad_id);
      const historyList = response.data?.data || [];
      setHistoryData(historyList);

      // 모든 키워드 ID 수집
      const keywordIds = new Set();
      historyList.forEach((history) => {
        if (history.beforeSnapshot) {
          if (history.beforeSnapshot.mainKeywordId) {
            keywordIds.add(history.beforeSnapshot.mainKeywordId);
          }
          if (history.beforeSnapshot.subKeywordIds) {
            history.beforeSnapshot.subKeywordIds.forEach((id) =>
              keywordIds.add(id),
            );
          }
        }
        if (history.afterSnapshot) {
          if (history.afterSnapshot.mainKeywordId) {
            keywordIds.add(history.afterSnapshot.mainKeywordId);
          }
          if (history.afterSnapshot.subKeywordIds) {
            history.afterSnapshot.subKeywordIds.forEach((id) =>
              keywordIds.add(id),
            );
          }
        }
      });

      // 키워드 이름 가져오기
      const map = {};
      // 현재 로드된 키워드 데이터에서 맵 생성
      if (keywordData && keywordData.length > 0) {
        keywordData.forEach((keyword) => {
          if (keyword.keyword_id) {
            map[keyword.keyword_id] = keyword.name;
          }
        });
      }

      // 아직 맵에 없는 키워드 ID들만 조회
      const missingKeywordIds = Array.from(keywordIds).filter((id) => !map[id]);
      if (missingKeywordIds.length > 0) {
        try {
          const keywordResponse = await getKeywordNameAPI(
            null,
            null,
            missingKeywordIds,
            1,
            2000,
          );
          const keywords = keywordResponse.data?.data || [];
          keywords.forEach((keyword) => {
            if (keyword.keyword_id && !map[keyword.keyword_id]) {
              map[keyword.keyword_id] = keyword.name;
            }
          });
        } catch (error) {
          console.error("키워드 이름 로드 실패:", error);
        }
      }

      setKeywordMap(map);
    } catch (error) {
      console.error("내역 데이터 로드 실패:", error);
      setHistoryData([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // 내역 펼치기/접기 토글
  const toggleHistoryExpanded = (historyId) => {
    setExpandedHistoryIds((prev) => {
      // 시안과 동일: 한 번에 하나만 펼침 (리스트 스크롤로 높이 확보)
      if (prev.has(historyId)) {
        return new Set();
      }
      return new Set([historyId]);
    });
  };

  // 액션 타입 한글 변환
  const getActionLabel = (action) => {
    switch (action) {
      case "STATUS_CHANGED":
        return "상태 변경";
      case "UPDATED":
      case "QUANTITY_CHANGED":
      case "PRICE_CHANGED":
      case "DATE_CHANGED":
        return "업데이트";
      case "CREATED":
        return "생성";
      default:
        return action;
    }
  };

  // 상태 한글 변환
  const getStatusLabel = (status) => {
    switch (status) {
      case "PENDING":
        return "승인대기중";
      case "APPROVED":
        return "진행중";
      case "PAUSED":
        return "일시정지";
      case "REJECTED":
        return "거절";
      case "DONE":
        return "종료";
      case "REFUND":
        return "환불";
      default:
        return status;
    }
  };

  if (!isOpen || !adData) return null;

  const getStatusText = (status) => {
    switch (status) {
      case "APPROVED":
        return "진행중";
      case "PAUSED":
        return "일시정지";
      case "PENDING":
        return "승인대기중";
      case "REJECTED":
        return "거절";
      case "REFUND":
        return "환불";
      case "DONE":
        return "종료";
      default:
        return status;
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "APPROVED":
        return "status-progress";
      case "PENDING":
        return "status-pending";
      case "PAUSED":
        return "status-paused";
      case "REJECTED":
        return "status-cancelled";
      case "REFUND":
        return "status-refund";
      case "DONE":
        return "status-ended";
      default:
        return "";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return dateString.split("T")[0].replace(/-/g, ".");
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return "-";
    return num.toLocaleString();
  };

  const TotalPrice = () => {
    const price = adData.price || 0;
    return formatNumber(price);
  };

  // 서브키워드 배열 생성 (모든 서브키워드 포함)
  const getSubKeywords = () => {
    // keywordData가 배열인 경우 SUB 타입 키워드들을 추출
    const subKeywords = keywordData?.filter((k) => k.type === "SUB") || [];
    return subKeywords.map((keyword) => keyword.name || "-");
  };

  // 광고 종료일(endDate)까지 남은 일수 (날짜만 비교)
  const getRemainingDaysUntilEnd = () => {
    if (!adData?.endDate) return 0;
    const end = new Date(adData.endDate);
    const endDateOnly = new Date(
      end.getFullYear(),
      end.getMonth(),
      end.getDate(),
    );
    const now = new Date();
    const todayOnly = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    );
    return Math.floor((endDateOnly - todayOnly) / (24 * 60 * 60 * 1000));
  };

  // 광고 취소 핸들러
  const handleCancelAd = async () => {
    if (!adData?.ad_id) {
      alert("광고 정보를 찾을 수 없습니다.");
      return;
    }

    try {
      setIsCancelling(true);

      // 광고 상태 재확인
      const checkResponse = await getMemberAdAPI(
        user?.id || adData.webMemberId || adData.member_id,
        1,
        1,
        { ad_id: adData.ad_id },
      );

      const ads = checkResponse.data?.data || [];
      const currentAd = ads.find((ad) => ad.ad_id === adData.ad_id);

      if (!currentAd) {
        alert("광고를 찾을 수 없습니다.");
        setIsCancelling(false);
        return;
      }

      // status가 "PENDING"이 아니면 취소 불가
      if (currentAd.status !== "PENDING") {
        alert(
          `현재 광고 상태가 "${getStatusText(
            currentAd.status,
          )}"이므로 등록 취소할 수 없습니다.`,
        );
        setIsCancelling(false);
        // 상태가 변경되었을 수 있으므로 데이터 새로고침
        if (onSave) {
          onSave();
        }
        return;
      }

      const confirmCancel = window.confirm(
        "정말로 이 광고를 취소하시겠습니까?",
      );
      if (!confirmCancel) {
        setIsCancelling(false);
        return;
      }

      // rejectAdAPI 호출 (필요한 파라미터들 전달)
      const currentDeposit = user?.deposit || 0; // 로그인한 사용자의 현재 예치금
      const amount = currentAd.price || adData.price || 0;
      const newDeposit = currentDeposit + amount;

      await rejectAdAPI(
        currentAd.ad_id,
        currentAd.webMemberId || currentAd.member_id,
        currentAd.missionName || currentAd.adName || "광고",
        newDeposit,
        amount,
        "REJECTED",
        currentAd.startDate,
        currentAd.endDate,
      );

      alert("광고가 성공적으로 취소되었습니다.");
      onClose(); // 모달 닫기
      // 부모 컴포넌트에서 데이터 새로고침을 위해 onSave 호출
      if (onSave) {
        onSave();
      }
    } catch (error) {
      console.error("광고 취소 실패:", error);
      alert("광고 취소에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setIsCancelling(false);
    }
  };

  // 광고 데이터 새로고침 처리
  const handleRefreshAdData = async () => {
    if (!adData) {
      alert("광고 데이터를 찾을 수 없습니다.");
      return;
    }

    try {
      setRefreshLoading(true);
      // missionTemplate이 1 (쇼핑 방문) 또는 2 (쇼핑 원부 방문)일 수 있음
      // adData에서 missionTemplate 정보 확인 필요
      const missionTemplate =
        adData.missionTemplate || (adData.adType === 2 ? 2 : 1);

      if (missionTemplate === 1 && adData.productUrl) {
        const response = await getNaverSmartStoreAPI(adData.productUrl);
        if (response.data.error) {
          console.error("크롤링 에러:", response.data.error);
          alert(`크롤링 실패: ${response.data.error}`);
          setRefreshLoading(false);
          return;
        }

        if (response.data.success && response.data.data) {
          let memoData = {};
          try {
            if (adData.memo) {
              memoData = JSON.parse(adData.memo);
            }
          } catch (error) {
            console.error("memo 파싱 실패:", error);
          }

          const hasImageUrlChanged =
            adData.imageUrl !== response.data.data.imageUrl;
          const hasMissionNameChanged =
            adData.missionName !== response.data.data.productName;
          const hasOriginProductNameChanged =
            memoData.originProductName !== response.data.data.productName;
          const hasOriginPriceChanged =
            memoData.originPrice !== response.data.data.price;
          const hasAdTagChanged = memoData.adTag !== response.data.data.tags;

          if (
            hasImageUrlChanged ||
            hasMissionNameChanged ||
            hasOriginProductNameChanged ||
            hasOriginPriceChanged ||
            hasAdTagChanged
          ) {
            const updatedMemoData = { ...memoData };
            if (hasOriginPriceChanged) {
              updatedMemoData.originPrice = response.data.data.price;
            }
            if (hasAdTagChanged) {
              updatedMemoData.adTag = response.data.data.tags;
            }
            if (hasOriginProductNameChanged) {
              updatedMemoData.originProductName =
                response.data.data.productName;
            }

            const extensionDataFix = {
              ad_id: adData.ad_id || adData.id,
              imageUrl: hasImageUrlChanged
                ? response.data.data.imageUrl
                : adData.imageUrl,
              missionName: hasMissionNameChanged
                ? response.data.data.productName
                : adData.missionName,
              memo: JSON.stringify(updatedMemoData),
            };

            // PAUSED 상태일 때 처리
            if (adData.status === "PAUSED") {
              extensionDataFix.status = "APPROVED";

              // pausedDate 배열 확인
              let pausedDateArray = updatedMemoData.pausedDate;
              if (!Array.isArray(pausedDateArray)) {
                // 배열이 아니면 빈 배열로 초기화
                pausedDateArray = [];
              }

              // pausedDate 배열의 마지막 항목 확인
              const lastPausedItem =
                pausedDateArray.length > 0
                  ? pausedDateArray[pausedDateArray.length - 1]
                  : null;

              // 마지막 항목이 있고, pausedDate가 있으며, changeToApproved가 아직 없는 경우에만 처리
              if (
                lastPausedItem &&
                lastPausedItem.pausedDate &&
                !lastPausedItem.changeToApproved
              ) {
                // changeToApproved: UTC 현재 시간
                const changeToApproved = new Date().toISOString();

                // pausedDate와 changeToApproved 날짜 추출 (당일 제외 계산을 위해)
                const pausedDateStr = lastPausedItem.pausedDate.includes("T")
                  ? lastPausedItem.pausedDate.split("T")[0]
                  : lastPausedItem.pausedDate.split(" ")[0];
                const changeToApprovedStr = changeToApproved.includes("T")
                  ? changeToApproved.split("T")[0]
                  : changeToApproved.split(" ")[0];

                // pausedDate와 changeToApproved 날짜 객체 생성
                const pausedDate = new Date(pausedDateStr + "T00:00:00");
                const changeToApprovedDate = new Date(
                  changeToApprovedStr + "T00:00:00",
                );

                // 당일 제외하고 날짜 차이 계산
                // 예: pausedDate가 12/03, changeToApproved가 12/05면
                // 12/04만 계산 (12/03과 12/05는 제외)
                const daysDiff = Math.floor(
                  (changeToApprovedDate - pausedDate) / (1000 * 60 * 60 * 24),
                );

                // 당일 제외: daysDiff가 1보다 크면 1을 빼고, 1 이하면 0
                const actualDays = daysDiff > 1 ? daysDiff - 1 : 0;

                // dailyQuantity와 cost 가져오기
                const dailyQuantity =
                  lastPausedItem.dailyQuantity || adData.dailyQuantity || 0;
                const cost =
                  lastPausedItem.cost ||
                  (adData.price && adData.totalQuantity
                    ? Math.round(adData.price / adData.totalQuantity)
                    : 0);

                // amount 계산: actualDays * dailyQuantity * cost
                const amount = actualDays * dailyQuantity * cost;

                // 마지막 항목에 changeToApproved와 amount 추가
                pausedDateArray[pausedDateArray.length - 1] = {
                  ...lastPausedItem,
                  changeToApproved: changeToApproved,
                  amount: amount,
                };

                // updatedMemoData에 업데이트된 pausedDate 배열 저장
                updatedMemoData.pausedDate = pausedDateArray;
                extensionDataFix.memo = JSON.stringify(updatedMemoData);
              } else {
                // pausedDate 배열이 없거나 마지막 항목이 없으면 새로 생성
                const dailyQuantity = adData.dailyQuantity || 0;
                const cost =
                  adData.price && adData.totalQuantity
                    ? Math.round(adData.price / adData.totalQuantity)
                    : 0;
                const changeToApproved = new Date().toISOString();
                const pausedDateValue = adData.updatedAt || changeToApproved;

                pausedDateArray.push({
                  pausedDate: pausedDateValue,
                  dailyQuantity: dailyQuantity,
                  cost: cost,
                  changeToApproved: changeToApproved,
                  amount: 0, // 당일이므로 0
                });

                updatedMemoData.pausedDate = pausedDateArray;
                extensionDataFix.memo = JSON.stringify(updatedMemoData);
              }
            }

            try {
              const updateResponse =
                await patchAdExtensionAPI(extensionDataFix);
              if (response.data.success && response.data.data) {
                console.log("updateResponse", updateResponse);
              }
              alert("데이터가 성공적으로 업데이트되었습니다.");
              if (onSave) {
                await onSave();
              }
            } catch (updateError) {
              console.error("업데이트 실패:", updateError);
              alert("업데이트 중 오류가 발생했습니다.");
            }
          } else {
            alert("변경할 데이터가 없습니다.");
            setRefreshLoading(false);
            return;
          }
        }
      } else if (missionTemplate === 2 && adData.productUrl) {
        const response = await getNaverSmartStoreAPI(adData.productUrl);
        if (response.data.error) {
          console.error("크롤링 에러:", response.data.error);
          alert(`크롤링 실패: ${response.data.error}`);
          setRefreshLoading(false);
          return;
        }

        if (response.data.success && response.data.data) {
          let memoData = {};
          try {
            if (adData.memo) {
              memoData = JSON.parse(adData.memo);
            }
          } catch (error) {
            console.error("memo 파싱 실패:", error);
          }

          // 원부 정보는 등록 때와 같은 기준으로 받아온다
          // 검색어: 원부 상품명(missionName), 상품 ID: 원부 URL(originId)에서 추출
          const originProductId = extractProductIdFromUrl(adData.originId);
          if (!adData.missionName || !originProductId) {
            alert(
              "원부 상품명 또는 원부 URL이 없어 원부 정보를 받아올 수 없습니다.",
            );
            setRefreshLoading(false);
            return;
          }

          const responseOrigin = await getOriginalInfoMissionAPI(
            adData.missionName,
            originProductId,
          );

          if (!responseOrigin.data?.success || !responseOrigin.data?.data) {
            const originError =
              responseOrigin.data?.error || "원부 상품을 찾을 수 없습니다";
            console.error("원부 크롤링 에러:", originError);
            alert(`원부 크롤링 실패: ${originError}`);
            setRefreshLoading(false);
            return;
          }

          const storeData = response.data.data;
          const originData = responseOrigin.data.data;
          const originMemo = pickOriginInfoMemo(originData);
          const originName = decodeHtmlText(originData.productName) || "";
          const originImageUrl = decodeHtmlText(originData.imageUrl) || "";

          // 저장 규칙은 utils/helpers.js ORIGIN_INFO_MEMO_KEYS 주석 참고
          // - missionName / imageUrl: 원부 정보 API 값 (빈 값이면 유지)
          // - memo.originProductName / originPrice / adTag / imageUrl: 스마트스토어 parse-link 값
          const hasMissionNameChanged =
            !!originName && adData.missionName !== originName;
          const hasImageUrlChanged =
            !!originImageUrl && adData.imageUrl !== originImageUrl;
          const hasStoreImageChanged =
            memoData.imageUrl !== (storeData.imageUrl || "");
          const hasOriginProductNameChanged =
            memoData.originProductName !== storeData.productName;
          const hasOriginPriceChanged = memoData.originPrice !== storeData.price;
          const hasAdTagChanged = memoData.adTag !== (storeData.tags || "");
          const hasOriginInfoChanged = ORIGIN_INFO_MEMO_KEYS.some(
            (key) => memoData[key] !== originMemo[key],
          );

          if (
            hasMissionNameChanged ||
            hasImageUrlChanged ||
            hasStoreImageChanged ||
            hasOriginProductNameChanged ||
            hasOriginPriceChanged ||
            hasAdTagChanged ||
            hasOriginInfoChanged
          ) {
            const updatedMemoData = {
              ...memoData,
              originProductName: storeData.productName,
              originPrice: storeData.price,
              adTag: storeData.tags || "",
              imageUrl: storeData.imageUrl || "",
              ...originMemo,
            };

            const extensionDataFix = {
              ad_id: adData.ad_id || adData.id,
              memo: JSON.stringify(updatedMemoData),
            };
            if (hasMissionNameChanged) {
              extensionDataFix.missionName = originName;
            }
            if (hasImageUrlChanged) {
              extensionDataFix.imageUrl = originImageUrl;
            }

            // PAUSED 상태일 때 처리
            if (adData.status === "PAUSED") {
              extensionDataFix.status = "APPROVED";

              // pausedDate 배열 확인
              let pausedDateArray = updatedMemoData.pausedDate;
              if (!Array.isArray(pausedDateArray)) {
                // 배열이 아니면 빈 배열로 초기화
                pausedDateArray = [];
              }

              // pausedDate 배열의 마지막 항목 확인
              const lastPausedItem =
                pausedDateArray.length > 0
                  ? pausedDateArray[pausedDateArray.length - 1]
                  : null;

              // 마지막 항목이 있고, pausedDate가 있으며, changeToApproved가 아직 없는 경우에만 처리
              if (
                lastPausedItem &&
                lastPausedItem.pausedDate &&
                !lastPausedItem.changeToApproved
              ) {
                // changeToApproved: UTC 현재 시간
                const changeToApproved = new Date().toISOString();

                // pausedDate와 changeToApproved 날짜 추출 (당일 제외 계산을 위해)
                const pausedDateStr = lastPausedItem.pausedDate.includes("T")
                  ? lastPausedItem.pausedDate.split("T")[0]
                  : lastPausedItem.pausedDate.split(" ")[0];
                const changeToApprovedStr = changeToApproved.includes("T")
                  ? changeToApproved.split("T")[0]
                  : changeToApproved.split(" ")[0];

                // pausedDate와 changeToApproved 날짜 객체 생성
                const pausedDate = new Date(pausedDateStr + "T00:00:00");
                const changeToApprovedDate = new Date(
                  changeToApprovedStr + "T00:00:00",
                );

                // 당일 제외하고 날짜 차이 계산
                // 예: pausedDate가 12/03, changeToApproved가 12/05면
                // 12/04만 계산 (12/03과 12/05는 제외)
                const daysDiff = Math.floor(
                  (changeToApprovedDate - pausedDate) / (1000 * 60 * 60 * 24),
                );

                // 당일 제외: daysDiff가 1보다 크면 1을 빼고, 1 이하면 0
                const actualDays = daysDiff > 1 ? daysDiff - 1 : 0;

                // dailyQuantity와 cost 가져오기
                const dailyQuantity =
                  lastPausedItem.dailyQuantity || adData.dailyQuantity || 0;
                const cost =
                  lastPausedItem.cost ||
                  (adData.price && adData.totalQuantity
                    ? Math.round(adData.price / adData.totalQuantity)
                    : 0);

                // amount 계산: actualDays * dailyQuantity * cost
                const amount = actualDays * dailyQuantity * cost;

                // 마지막 항목에 changeToApproved와 amount 추가
                pausedDateArray[pausedDateArray.length - 1] = {
                  ...lastPausedItem,
                  changeToApproved: changeToApproved,
                  amount: amount,
                };

                // updatedMemoData에 업데이트된 pausedDate 배열 저장
                updatedMemoData.pausedDate = pausedDateArray;
                extensionDataFix.memo = JSON.stringify(updatedMemoData);
              } else {
                // pausedDate 배열이 없거나 마지막 항목이 없으면 새로 생성
                const dailyQuantity = adData.dailyQuantity || 0;
                const cost =
                  adData.price && adData.totalQuantity
                    ? Math.round(adData.price / adData.totalQuantity)
                    : 0;
                const changeToApproved = new Date().toISOString();
                const pausedDateValue = adData.updatedAt || changeToApproved;

                pausedDateArray.push({
                  pausedDate: pausedDateValue,
                  dailyQuantity: dailyQuantity,
                  cost: cost,
                  changeToApproved: changeToApproved,
                  amount: 0, // 당일이므로 0
                });

                updatedMemoData.pausedDate = pausedDateArray;
                extensionDataFix.memo = JSON.stringify(updatedMemoData);
              }
            }

            try {
              const updateResponse =
                await patchAdExtensionAPI(extensionDataFix);
              if (response.data.success && response.data.data) {
                console.log("updateResponse", updateResponse);
              }
              alert("데이터가 성공적으로 업데이트되었습니다.");
              if (onSave) {
                await onSave();
              }
            } catch (updateError) {
              console.error("업데이트 실패:", updateError);
              alert("업데이트 중 오류가 발생했습니다.");
            }
          } else {
            alert("변경할 데이터가 없습니다.");
            setRefreshLoading(false);
            return;
          }
        }
      }
    } catch (error) {
      console.error("변경사항 발견 광고 데이터 새로고침 실패:", error);
      alert("데이터를 새로고침하는데 실패했습니다. 다시 시도해주세요.");
    } finally {
      setRefreshLoading(false);
    }
  };

  const mainKeywordForRank = keywordData?.find((k) => k.type === "MAIN");
  const initialRank =
    mainKeywordForRank?.initialRank ??
    adData.initialRank ??
    adData.startRank;
  const currentRank =
    mainKeywordForRank?.currentRank ?? adData.currentRank ?? adData.rank;
  const rankDelta =
    initialRank !== undefined &&
    currentRank !== undefined &&
    !Number.isNaN(Number(initialRank)) &&
    !Number.isNaN(Number(currentRank))
      ? Number(initialRank) - Number(currentRank)
      : null;

  return (
    <>
      <div className="ad-detail-modal-overlay" onClick={onClose}>
        <div
          className="ad-detail-modal ad-detail-shell"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Hero */}
          <section className="ad-detail-hero" aria-label="광고 요약">
            <img
              className="ad-detail-hero-thumb"
              src={adData.imageUrl || "/logo192.png"}
              alt={adData.missionName || "상품 이미지"}
              onError={(e) => {
                e.target.src = "/logo192.png";
              }}
            />
            <div className="ad-detail-hero-main">
              <div className="ad-detail-hero-store">
                {adData.storeName || "-"}
              </div>
              <div
                className="ad-detail-hero-name"
                title={adData.missionName || "-"}
              >
                {adData.missionName || "-"}
              </div>
              <div className="ad-detail-hero-meta">
                <RemainingDaysBadge
                  startDate={adData.startDate}
                  endDate={adData.endDate}
                  status={adData.status}
                  withParens
                />
                <span
                  className={`ad-detail-chip is-status ${getStatusClass(
                    adData.status,
                  )}`}
                >
                  {getStatusText(adData.status)}
                </span>
              </div>
            </div>
            <div className="ad-detail-hero-actions">
              {user?.role !== "CHECKER" &&
                (adData.status === "PENDING" ||
                  adData.status === "PAUSED" ||
                  adData.status === "APPROVED") && (
                  <button
                    type="button"
                    className="ad-detail-btn ad-detail-btn-refresh"
                    onClick={handleRefreshAdData}
                    disabled={refreshLoading}
                    title="다시 받아오기"
                  >
                    {refreshLoading ? "받아오는 중..." : "다시 받아오기"}
                  </button>
                )}
              {user?.role !== "CHECKER" && (
                <button
                  type="button"
                  className={`ad-detail-btn ad-detail-btn-extend${
                    isExtensionLocked ? " is-locked" : ""
                  }`}
                  disabled={adData.status !== "APPROVED"}
                  onClick={() => {
                    if (isExtensionLocked) {
                      alert(
                        `광고 연장은 마감당일 ${resolveEndSubmitTimeForDate(adData?.endDate, zoomSettings)} 이후 불가능합니다.`,
                      );
                      return;
                    }
                    setIsExtensionModalOpen(true);
                  }}
                >
                  광고연장
                </button>
              )}
            </div>
          </section>

          <TabNavigation
            className="ad-detail-segment-tabs"
            tabs={[
              { id: "details", label: "광고 상세 정보" },
              ...(user?.role !== "CHECKER"
                ? [
                    { id: "keywords", label: "키워드 & 순위" },
                    { id: "history", label: "내역" },
                  ]
                : []),
            ]}
            activeTab={activeTab}
            onTabClick={setActiveTab}
          />

          <div className="modal-content ad-detail-body">
            {activeTab === "details" && (
              <div className="ad-detail-panel">
                <div className="ad-detail-detail-groups">
                  <section className="ad-detail-group">
                    <h2 className="ad-detail-group-title">광고 정보</h2>
                    <div className="ad-detail-tiles">
                      <div className="ad-detail-tile is-wide">
                        <span className="ad-detail-tile-label">광고명</span>
                        <strong
                          className="ad-detail-tile-value"
                          title={adData.missionName || "-"}
                        >
                          {adData.missionName || "-"}
                        </strong>
                      </div>
                      <div className="ad-detail-tile">
                        <span className="ad-detail-tile-label">카테고리</span>
                        <strong className="ad-detail-tile-value">
                          {adData.category || "-"}
                        </strong>
                      </div>
                      <div className="ad-detail-tile">
                        <span className="ad-detail-tile-label">스토어명</span>
                        <strong className="ad-detail-tile-value">
                          {adData.storeName || "-"}
                        </strong>
                      </div>
                      {user?.role !== "CHECKER" && (
                        <div className="ad-detail-tile">
                          <span className="ad-detail-tile-label">미션 방법</span>
                          <strong className="ad-detail-tile-value">
                            {adData.productName || "-"}
                          </strong>
                        </div>
                      )}
                    </div>
                  </section>

                  <section className="ad-detail-group">
                    <h2 className="ad-detail-group-title">기간</h2>
                    <div className="ad-detail-tiles">
                      <div className="ad-detail-tile">
                        <span className="ad-detail-tile-label">광고 시작일</span>
                        <strong className="ad-detail-tile-value">
                          {formatDate(adData.startDate)}
                        </strong>
                      </div>
                      <div className="ad-detail-tile">
                        <span className="ad-detail-tile-label">광고 종료일</span>
                        <strong className="ad-detail-tile-value">
                          {formatDate(adData.endDate)}
                        </strong>
                      </div>
                      <div className="ad-detail-tile">
                        <span className="ad-detail-tile-label">마감 예정일</span>
                        <strong
                          className="ad-detail-tile-value"
                          title={formatDate(adData.endDate)}
                        >
                          {formatDate(adData.endDate)}
                        </strong>
                      </div>
                    </div>
                  </section>

                  <section className="ad-detail-group">
                    <h2 className="ad-detail-group-title">수량 · 결제</h2>
                    <div className="ad-detail-tiles">
                      {user?.role !== "CHECKER" && (
                        <div className="ad-detail-tile is-accent">
                          <span className="ad-detail-tile-label">1회 단가</span>
                          <strong
                            className="ad-detail-tile-value"
                            title={`${formatNumber(
                              Math.round(adData.price / adData.totalQuantity),
                            )}원`}
                          >
                            {formatNumber(
                              Math.round(adData.price / adData.totalQuantity),
                            )}
                            원
                          </strong>
                        </div>
                      )}
                      <div className="ad-detail-tile">
                        <span className="ad-detail-tile-label">총 수량</span>
                        <strong
                          className="ad-detail-tile-value"
                          title={formatNumber(adData.totalQuantity)}
                        >
                          {formatNumber(adData.totalQuantity)}
                        </strong>
                      </div>
                      <div className="ad-detail-tile">
                        <span className="ad-detail-tile-label">일일 수량</span>
                        <strong
                          className="ad-detail-tile-value"
                          title={formatNumber(adData.dailyQuantity)}
                        >
                          {formatNumber(adData.dailyQuantity)}
                        </strong>
                      </div>
                      {user?.role !== "CHECKER" && (
                        <div className="ad-detail-tile is-accent">
                          <span className="ad-detail-tile-label">결제 금액</span>
                          <strong
                            className="ad-detail-tile-value"
                            title={`${TotalPrice()}원`}
                          >
                            {TotalPrice()}원
                          </strong>
                        </div>
                      )}
                    </div>
                  </section>
                </div>

                <div className="modal-footer ad-detail-footer-actions">
                  {user?.role !== "CHECKER" &&
                    (adData.status === "APPROVED" ||
                      adData.status === "PENDING") && (
                      <button
                        type="button"
                        className="ad-detail-btn ad-detail-btn-outline"
                        onClick={() => {
                          setIsKeywordEditModalOpen(true);
                        }}
                      >
                        키워드 수정
                      </button>
                    )}
                  {user?.role !== "CHECKER" && adData.status === "APPROVED" && (
                    <button
                      type="button"
                      className="ad-detail-btn ad-detail-btn-outline"
                      onClick={() => {
                        if (getRemainingDaysUntilEnd() < 4) {
                          alert(
                            "수량 증가는 광고 종료일이 5일 이상 남았을 때만 가능합니다.",
                          );
                          return;
                        }
                        setIsQuantityIncreaseModalOpen(true);
                      }}
                    >
                      수량 증가
                    </button>
                  )}
                  {user?.role !== "CHECKER" && adData.status === "PENDING" && (
                    <button
                      type="button"
                      className="ad-detail-btn ad-detail-btn-danger"
                      onClick={handleCancelAd}
                      disabled={isCancelling}
                    >
                      {isCancelling ? "취소 중..." : "등록 취소"}
                    </button>
                  )}
                  {user?.role !== "CHECKER" && adData.status === "APPROVED" && (
                    <button
                      type="button"
                      className="ad-detail-btn ad-detail-btn-danger"
                      onClick={() => {
                        if (getRemainingDaysUntilEnd() < 3) {
                          alert(
                            "광고 종료 신청은 광고 종료일이 3일 이상 남았을 때만 가능합니다.",
                          );
                          return;
                        }
                        setIsAdEndRequestModalOpen(true);
                      }}
                      disabled={getRemainingDaysUntilEnd() < 3}
                    >
                      광고 종료 신청
                    </button>
                  )}
                </div>
              </div>
            )}

            {activeTab === "keywords" && (
              <div className="ad-detail-panel">
                <div className="ad-detail-kw-split">
                  <section className="ad-detail-section" aria-label="키워드">
                    <h2 className="ad-detail-group-title">키워드</h2>
                    <div className="ad-detail-kw-block">
                      <div className="ad-detail-kw-card ad-detail-kw-main-card">
                        <span className="ad-detail-eyebrow">메인 키워드</span>
                        <strong className="ad-detail-kw-main">
                          {mainKeywordForRank?.name ||
                            adData.mainKeyword ||
                            "없음"}
                        </strong>
                      </div>
                      <div className="ad-detail-kw-card ad-detail-kw-sub-card">
                        <span className="ad-detail-eyebrow">서브 키워드</span>
                        <div className="ad-detail-kw-tags">
                          {getSubKeywords().length > 0 ? (
                            getSubKeywords().map((keyword, index) => (
                              <span key={index} className="ad-detail-kw-tag">
                                {keyword}
                              </span>
                            ))
                          ) : (
                            <span className="ad-detail-kw-empty">없음</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </section>

                  <section className="ad-detail-section" aria-label="순위">
                    <h2 className="ad-detail-group-title">순위</h2>
                    <div className="ad-detail-kw-card ad-detail-rank-card">
                      <div className="ad-detail-rank-track">
                        <div className="ad-detail-rank-col">
                          <span className="ad-detail-rank-label">시작</span>
                          <strong className="ad-detail-rank-num">
                            {initialRank !== undefined && initialRank !== null
                              ? `${initialRank}위`
                              : "-"}
                          </strong>
                        </div>
                        <span className="ad-detail-rank-arrow" aria-hidden>
                          →
                        </span>
                        <div className="ad-detail-rank-col">
                          <span className="ad-detail-rank-label">현재</span>
                          <strong className="ad-detail-rank-num is-current">
                            {currentRank !== undefined && currentRank !== null
                              ? `${currentRank}위`
                              : "-"}
                          </strong>
                        </div>
                        {rankDelta !== null && (
                          <span
                            className={`ad-detail-rank-delta ${
                              rankDelta > 0
                                ? "is-up"
                                : rankDelta < 0
                                  ? "is-down"
                                  : ""
                            }`}
                          >
                            {rankDelta > 0
                              ? `${rankDelta}위 상승`
                              : rankDelta < 0
                                ? `${Math.abs(rankDelta)}위 하락`
                                : "변동 없음"}
                          </span>
                        )}
                        <button
                          type="button"
                          className="ad-detail-btn ad-detail-btn-outline ad-detail-rank-detail-btn"
                          onClick={() => setIsRankCheckModalOpen(true)}
                        >
                          순위 상세 보기
                        </button>
                      </div>
                    </div>
                  </section>
                </div>

                {user?.role !== "CHECKER" &&
                  (adData.status === "APPROVED" ||
                    adData.status === "PENDING") && (
                    <div className="modal-footer ad-detail-footer-actions">
                      <button
                        type="button"
                        className="ad-detail-btn ad-detail-btn-outline"
                        onClick={() => {
                          setIsKeywordEditModalOpen(true);
                        }}
                      >
                        키워드 수정
                      </button>
                    </div>
                  )}
              </div>
            )}

            {activeTab === "history" && (
              <div className="ad-detail-panel">
                <div className="ad-detail-history-head">
                  <h2 className="ad-detail-group-title">변경 내역</h2>
                  {!historyLoading && (
                    <span className="ad-detail-history-count">
                      {historyData.length}건
                    </span>
                  )}
                </div>
                {historyLoading ? (
                  <div className="ad-detail-history-empty">
                    <strong>내역을 불러오는 중...</strong>
                  </div>
                ) : historyData.length === 0 ? (
                  <div className="ad-detail-history-empty">
                    <strong>변경 내역이 없습니다</strong>
                    <p>광고 수정·상태 변경이 발생하면 이곳에 표시됩니다.</p>
                  </div>
                ) : (
                  <div
                    className={`ad-detail-history-list ${
                      historyData.length > 3 ? "is-many" : ""
                    }`}
                  >
                    {historyData.map((history) => {
                      const isExpanded = expandedHistoryIds.has(history._id);
                      const changedAt = history.changedAt
                        ? convertUTCToKoreaTime(history.changedAt)
                        : null;
                      const changedAtStr = changedAt
                        ? changedAt.toLocaleString("ko-KR", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "";

                      return (
                        <article
                          key={history._id}
                          className={`ad-detail-history-item ${
                            isExpanded ? "is-open" : ""
                          }`}
                        >
                          <button
                            type="button"
                            className="ad-detail-history-trigger"
                            onClick={() =>
                              toggleHistoryExpanded(history._id)
                            }
                            aria-expanded={isExpanded}
                          >
                            <span
                              className={`ad-detail-action-badge is-${String(
                                history.action || "",
                              ).toLowerCase()}`}
                            >
                              {getActionLabel(history.action)}
                            </span>
                            <span className="ad-detail-history-reason">
                              {history.reason || "-"}
                            </span>
                            {history.admin_memo ? (
                              <span className="ad-detail-history-memo">
                                ({history.admin_memo})
                              </span>
                            ) : null}
                            <span className="ad-detail-history-time">
                              {changedAtStr}
                            </span>
                            <span className="ad-detail-history-chevron">
                              {isExpanded ? "▾" : "▸"}
                            </span>
                          </button>

                          {isExpanded && (
                            <div className="ad-detail-history-detail">
                              {history.changedFields?.length > 0 && (
                                <div className="ad-detail-changed-fields">
                                  {history.changedFields.map((field) => (
                                    <span
                                      key={field}
                                      className="ad-detail-changed-tag"
                                    >
                                      {field}
                                    </span>
                                  ))}
                                </div>
                              )}
                              <div className="ad-detail-snap-grid">
                                <div className="ad-detail-snap is-before">
                                  <div className="ad-detail-snap-head">
                                    변경 전
                                  </div>
                                  <div className="ad-detail-snap-body">
                                    {history.beforeSnapshot ? (
                                      <HistorySnapshot
                                        snapshot={history.beforeSnapshot}
                                        changedFields={
                                          history.changedFields || []
                                        }
                                        keywordMap={keywordMap}
                                      />
                                    ) : (
                                      <p className="ad-detail-snap-empty">
                                        변경 전 데이터 없음
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <div className="ad-detail-snap is-after">
                                  <div className="ad-detail-snap-head">
                                    변경 후
                                  </div>
                                  <div className="ad-detail-snap-body">
                                    {history.afterSnapshot ? (
                                      <HistorySnapshot
                                        snapshot={history.afterSnapshot}
                                        changedFields={
                                          history.changedFields || []
                                        }
                                        keywordMap={keywordMap}
                                      />
                                    ) : (
                                      <p className="ad-detail-snap-empty">
                                        데이터 없음
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </article>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 광고 연장 모달을 최상위 레벨로 렌더링 */}
      {isExtensionModalOpen && (
        <AdExtensionModal
          isOpen={isExtensionModalOpen}
          onClose={() => setIsExtensionModalOpen(false)}
          selectedAds={[adData]}
          onExtend={(result) => {
            if (result === "success") {
              // 연장 성공 시 모달 닫기
              setIsExtensionModalOpen(false);
              // 부모 컴포넌트에 연장 완료 알림 및 데이터 새로고침
              alert("광고 연장이 성공적으로 완료되었습니다!");
              // 부모 컴포넌트의 데이터 새로고침 함수 호출
              if (onSave) {
                onSave();
              }
            }
          }}
          unitPrices={unitPrices} // unitPrices 데이터 전달
          username={username} // username 데이터 전달
          user={userData} // 가격 등급 데이터가 포함된 userData 전달
          getUserDetailAPI={getUserDetailAPI} // API 함수 전달
        />
      )}

      {/* 키워드 수정 모달 */}
      {isKeywordEditModalOpen && (
        <ShoppingKeywordEditModal
          isOpen={isKeywordEditModalOpen}
          onClose={() => setIsKeywordEditModalOpen(false)}
          adData={adData}
          keywordData={keywordData}
          onSave={onSave}
        />
      )}

      {/* 수량 증가 모달 */}
      {isQuantityIncreaseModalOpen && (
        <QuantityIncreaseModal
          isOpen={isQuantityIncreaseModalOpen}
          onClose={() => setIsQuantityIncreaseModalOpen(false)}
          adData={adData}
          unitPrice={getUnitPrice(adData)}
          onSave={onSave}
        />
      )}

      {/* 광고 종료 신청 모달 */}
      <AdEndRequestModal
        isOpen={isAdEndRequestModalOpen}
        onClose={() => setIsAdEndRequestModalOpen(false)}
        adData={adData}
        onSave={onSave}
        user={user}
        endSubmitTime={endSubmitTime}
      />

      {/* 순위체크 모달 */}
      <RankCheckModal
        isOpen={isRankCheckModalOpen}
        onClose={() => setIsRankCheckModalOpen(false)}
        adData={adData}
      />
    </>
  );
};

// 내역 스냅샷 컴포넌트
const HistorySnapshot = ({ snapshot, changedFields = [], keywordMap = {} }) => {
  const getStatusLabel = (status) => {
    switch (status) {
      case "PENDING":
        return "승인대기중";
      case "APPROVED":
        return "진행중";
      case "PAUSED":
        return "일시정지";
      case "REJECTED":
        return "거절";
      case "DONE":
        return "종료";
      case "REFUND":
        return "환불";
      default:
        return status;
    }
  };

  // 필드가 변경되었는지 확인
  const isChanged = (fieldName) => {
    return changedFields.includes(fieldName);
  };

  // 값 포맷팅 함수
  const formatValue = (value) => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "boolean") return value ? "예" : "아니오";
    if (Array.isArray(value)) return value.join(", ");
    return value;
  };

  // 날짜 포맷팅
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleString("ko-KR");
    } catch (error) {
      return dateString;
    }
  };

  let memoData = {};
  try {
    if (snapshot.memo) {
      memoData = JSON.parse(snapshot.memo);
    }
  } catch (error) {
    console.error("memo 파싱 실패:", error);
  }

  // 모든 필드를 표시하는 함수
  const renderField = (
    label,
    fieldName,
    value,
    formatter = null,
    forceChanged = false,
  ) => {
    const formattedValue = formatter ? formatter(value) : formatValue(value);
    const changed = forceChanged || isChanged(fieldName);

    return (
      <div className="snapshot-row" key={fieldName + label}>
        <span className="snapshot-label">{label}:</span>
        <span className={`snapshot-value ${changed ? "changed-field" : ""}`}>
          {formattedValue}
        </span>
      </div>
    );
  };

  return (
    <div className="snapshot-data">
      {renderField("서비스명", "serviceName", snapshot.serviceName)}
      {renderField("광고명", "missionName", snapshot.missionName)}
      {renderField("미션 브라우저", "openWebSite", snapshot.openWebSite)}
      {renderField("웹 회원 ID", "webMemberId", snapshot.webMemberId)}
      {renderField("상품명", "productName", snapshot.productName)}
      {renderField("스토어명", "storeName", snapshot.storeName)}
      {renderField("상품 URL", "productUrl", snapshot.productUrl)}
      {renderField("상품 ID", "productId", snapshot.productId)}
      {renderField("원부 ID", "originId", snapshot.originId)}
      {renderField("이미지 URL", "imageUrl", snapshot.imageUrl)}
      {renderField("상태", "status", getStatusLabel(snapshot.status))}
      {renderField(
        "가격",
        "price",
        snapshot.price ? `${snapshot.price.toLocaleString()}원` : "-",
      )}
      {renderField("총 수량", "totalQuantity", snapshot.totalQuantity)}
      {renderField("일일 수량", "dailyQuantity", snapshot.dailyQuantity)}
      {renderField("시작일", "startDate", formatDate(snapshot.startDate))}
      {renderField("종료일", "endDate", formatDate(snapshot.endDate))}
      {renderField(
        "메인 키워드",
        "mainKeywordId",
        snapshot.mainKeywordId
          ? `${keywordMap[snapshot.mainKeywordId] || "알 수 없음"} (ID: ${
              snapshot.mainKeywordId
            })`
          : "-",
      )}
      {renderField(
        "서브 키워드",
        "subKeywordIds",
        snapshot.subKeywordIds && snapshot.subKeywordIds.length > 0
          ? snapshot.subKeywordIds
              .map((id) => `${keywordMap[id] || "알 수 없음"} (ID: ${id})`)
              .join(", ")
          : "-",
      )}
      {renderField("미션 분할", "divisionCount", snapshot.divisionCount)}
      {renderField("생성일", "createdAt", formatDate(snapshot.createdAt))}
      {renderField("수정일", "updatedAt", formatDate(snapshot.updatedAt))}
      {/* Memo 필드들 */}
      {memoData.originProductName !== undefined &&
        renderField(
          "원본 상품명",
          "memo",
          memoData.originProductName,
          null,
          isChanged("memo"),
        )}
      {memoData.originPrice !== undefined &&
        renderField(
          "원가",
          "memo",
          memoData.originPrice,
          null,
          isChanged("memo"),
        )}
      {memoData.unitPrice !== undefined &&
        renderField(
          "적용단가",
          "memo",
          memoData.unitPrice,
          null,
          isChanged("memo"),
        )}
      {memoData.adTag !== undefined &&
        renderField(
          "광고 태그",
          "memo",
          memoData.adTag,
          null,
          isChanged("memo"),
        )}
    </div>
  );
};

export default ShoppingAdDetail;
